import { Link } from "react-router-dom";
import StatusBadge from "@/components/base/StatusBadge";
import type { Scoop } from "@/types";

interface ScoopCardProps {
  scoop: Scoop;
  variant?: "default" | "featured" | "row";
  index?: number;
}

function MetaLine({ scoop }: { scoop: Scoop }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
      <span className="flex items-center gap-1.5">
        <i className="ri-calendar-line" aria-hidden="true"></i>
        {scoop.date}
      </span>
      <span className="flex items-center gap-1.5">
        <i className="ri-eye-line" aria-hidden="true"></i>
        {scoop.reads.toLocaleString("zh-CN")} 次阅读
      </span>
      <span className="flex items-center gap-1.5">
        <i className="ri-map-pin-line" aria-hidden="true"></i>
        {scoop.location}
      </span>
    </div>
  );
}

export default function ScoopCard({ scoop, variant = "default", index }: ScoopCardProps) {
  const href = `/scoops/${scoop.id}`;

  if (variant === "featured") {
    return (
      <article className="group relative overflow-hidden rounded-lg border border-foreground-950/10 bg-foreground-950">
        <div className="h-[320px] w-full md:h-[420px]">
          <img
            src={scoop.cover}
            alt={`${scoop.title} - 看料爆料线索封面`}
            title={`${scoop.title} - 看料爆料`}
            className="h-full w-full object-cover opacity-80 transition-opacity duration-500 group-hover:opacity-95"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-transparent"></div>
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary-600 px-2.5 py-0.5 text-[11px] font-medium text-background-50">
              {scoop.categoryLabel}
            </span>
            <StatusBadge status={scoop.status} />
          </div>
          <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-background-50 md:text-2xl">
            <Link to={href} className="transition-colors hover:text-accent-200">
              {scoop.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 max-w-2xl text-sm leading-relaxed text-background-200">
            {scoop.excerpt}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-background-300">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" aria-hidden="true"></i>
              {scoop.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {scoop.reads.toLocaleString("zh-CN")} 次阅读
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === "row") {
    return (
      <article className="group flex gap-4 border-b border-foreground-950/10 py-5 last:border-b-0">
        {typeof index === "number" && (
          <span className="w-8 shrink-0 font-heading text-2xl font-bold leading-none text-foreground-300">
            {String(index + 1).padStart(2, "0")}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-primary-700">{scoop.categoryLabel}</span>
            <span className="h-3 w-px bg-foreground-950/15" aria-hidden="true"></span>
            <span className="text-xs text-foreground-500">{scoop.date}</span>
          </div>
          <h3 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950">
            <Link to={href} className="transition-colors hover:text-primary-700">
              {scoop.title}
            </Link>
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">{scoop.excerpt}</p>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-foreground-950/10 bg-background-50 transition-colors hover:border-primary-300">
      <div className="h-48 w-full overflow-hidden bg-background-200">
        <img
          src={scoop.cover}
          alt={`${scoop.title} - 看料爆料线索封面`}
          title={`${scoop.title} - 看料爆料`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] font-medium text-secondary-800">
            {scoop.categoryLabel}
          </span>
          <StatusBadge status={scoop.status} />
        </div>
        <h3 className="mt-3 font-heading text-lg font-semibold leading-snug text-foreground-950">
          <Link to={href} className="transition-colors hover:text-primary-700">
            {scoop.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">{scoop.excerpt}</p>
        <div className="mt-4 border-t border-foreground-950/10 pt-3">
          <MetaLine scoop={scoop} />
        </div>
      </div>
    </article>
  );
}